import { FC, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import {
  Button,
  Divider,
  IconButton,
  Input,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { WhiteWrapper } from '../styles';
import { ChipsWrapper } from './styles';
import Tag from './Tag';
import useAlertStore from '@/stores/alertStore';
import AddIcon from '@mui/icons-material/Add';
import { useUpdateNote } from '@/services/noteGoApi/noteGoApiComponents';
import { useAuthStore } from '@/stores/authStore';
import { useQueryClient } from '@tanstack/react-query';
import { useNoteGoApiContext } from '@/services/noteGoApi/noteGoApiContext';
import { getTagName } from '@/utils/tagUtils';
import { errorReporter } from '@/services/errorReportingService';

interface TagsProps {
  initialTags: (string | { id?: string; name: string; color?: string })[];
  isOwner: boolean;
  onTagsUpdated?: () => void;
}

const Tags: FC<TagsProps> = ({ initialTags, isOwner, onTagsUpdated }) => {
  const { noteId } = useParams();
  const showAlert = useAlertStore((state) => state.showAlert);
  const { userToken } = useAuthStore();
  const { mutateAsync: updateNote } = useUpdateNote();
  const queryClient = useQueryClient();
  const { queryKeyFn } = useNoteGoApiContext();
  const [newTag, setNewTag] = useState('');
  // Convert tags to strings for internal use, filtering out empty ones
  const normalizedInitialTags = (initialTags || []).map((tag) => getTagName(tag)).filter((tag) => tag.trim() !== '');
  const [tags, setTags] = useState<string[]>(normalizedInitialTags);
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [editTagId, setEditTagId] = useState<number | null>(null);
  const [menuOptionsAnchorEl, setOptionsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [remainingMenuOpen, setRemainingMenuOpen] = useState(false);
  const optionsMenuOpen = Boolean(menuOptionsAnchorEl);
  const indexedTags = tags.map((tag, index) => ({ tag, index }));
  const visibleTags = indexedTags?.slice(0, 5);
  const remainingTags = indexedTags?.slice(5);
  const remainingTagsCount = remainingTags?.length;

  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleOptionsMenuOpen = useCallback(
    (event: MouseEvent<HTMLDivElement>, tagId: number) => {
      if (!isOwner) return;
      setOptionsMenuAnchorEl(event.currentTarget);
      setSelectedTagId(tagId);
    },
    [isOwner],
  );

  const handleOptionsMenuClose = useCallback(() => {
    setOptionsMenuAnchorEl(null);
  }, []);

  const handleEditTag = () => {
    if (selectedTagId !== null) {
      setEditTagId(selectedTagId);
    }
    handleOptionsMenuClose();
  };

  const handleDeleteTag = async () => {
    if (selectedTagId !== null) {
      if (noteId) {
        await updateNote(
          {
            headers: { authorization: `Bearer ${userToken}` },
            pathParams: { noteId },
            body: {
              tags: tags?.filter((_, index) => index !== selectedTagId),
            },
          },

          {
            onSuccess: () => {
              setTags((prevTags) => prevTags?.filter((_, index) => index !== selectedTagId));
              showAlert('Tag deleted successfully.', 'success');

              // Invalidate query cache
              if (noteId && userToken) {
                const queryKey = queryKeyFn({
                  path: '/notes/{noteId}',
                  operationId: 'getNote',
                  variables: {
                    headers: { authorization: `Bearer ${userToken}` },
                    pathParams: { noteId },
                  },
                });
                queryClient.invalidateQueries({ queryKey });
              }

              // Refetch note data to get updated tags from server
              onTagsUpdated?.();
            },
            onError: () => {
              showAlert('Something went wrong while updating tags. Try again later.', 'error');
            },
          },
        );
      }
    }
    handleOptionsMenuClose();
  };

  const saveEdit = async (tagId: number, newLabel: string) => {
    if (!newLabel) {
      showAlert('Tag cannot be empty.', 'error');
      return;
    }
    const updatedTags = tags?.map((tag, index) => (index === tagId ? newLabel : tag));
    setTags(updatedTags);
    if (noteId) {
      await updateNote(
        {
          headers: { authorization: `Bearer ${userToken}` },
          pathParams: { noteId },
          body: {
            tags: updatedTags,
          },
        },
        {
          onSuccess: () => {
            // Invalidate query cache
            if (noteId && userToken) {
              const queryKey = queryKeyFn({
                path: '/notes/{noteId}',
                operationId: 'getNote',
                variables: {
                  headers: { authorization: `Bearer ${userToken}` },
                  pathParams: { noteId },
                },
              });
              queryClient.invalidateQueries({ queryKey });
            }

            // Refetch note data to get updated tags from server
            onTagsUpdated?.();
          },
          onError: () => {
            showAlert('Something went wrong while updating tags. Try again later.', 'error');
            // Revert the local state on error
            setTags(tags);
          },
        },
      );
    }
  };

  const handleCreateTag = async () => {
    if (!newTag) {
      showAlert('Tag cannot be empty.', 'error');
      return;
    }

    const updatedTags = [...tags, newTag];
    setTags(updatedTags);

    if (noteId) {
      try {
        await updateNote(
          {
            headers: { authorization: `Bearer ${userToken}` },
            pathParams: { noteId },
            body: {
              tags: updatedTags,
            },
          },
          {
            onSuccess: () => {
              showAlert('Tag added successfully.', 'success');

              // Invalidate and refetch the note query cache
              if (noteId && userToken) {
                const queryKey = queryKeyFn({
                  path: '/notes/{noteId}',
                  operationId: 'getNote',
                  variables: {
                    headers: { authorization: `Bearer ${userToken}` },
                    pathParams: { noteId },
                  },
                });
                queryClient.invalidateQueries({ queryKey });
              }

              // Also call the refetch callback
              onTagsUpdated?.();
            },
            onError: () => {
              showAlert('Something went wrong while updating tags. Try again later.', 'error');
              // Revert the local state on error
              setTags(tags);
            },
          },
        );
      } catch (err) {
        errorReporter.expected('Failed to update note tags', err, { feature: 'notes', operation: 'updateTags' });
        showAlert('Something went wrong while updating tags. Try again later.', 'error');
        setTags(tags);
      }
    }
    setNewTag('');
  };

  useEffect(() => {
    if (initialTags?.length > 0) {
      // Convert tags to strings when initialTags updates, filtering out empty ones
      const normalized = initialTags.map((tag) => getTagName(tag)).filter((tag) => tag.trim() !== '');
      setTags(normalized);
    }
  }, [initialTags]);

  const chipOptionsMenu = () => {
    return (
      <Menu
        id="basic-menu"
        anchorEl={menuOptionsAnchorEl}
        open={optionsMenuOpen}
        onClose={handleOptionsMenuClose}
        MenuListProps={{
          'aria-labelledby': 'options-menu',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{ marginTop: '8px' }}
      >
        <MenuItem onClick={handleEditTag} dense>
          <ListItemIcon>
            <Edit />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteTag} dense>
          <ListItemIcon>
            <Delete />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    );
  };

  const remainingChipsMenu = () => {
    return (
      <Menu
        id="remaining-chips-menu"
        anchorEl={wrapperRef.current}
        open={remainingMenuOpen}
        onClose={() => setRemainingMenuOpen(false)}
        MenuListProps={{
          'aria-labelledby': 'remaining-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {remainingTags?.map((tag) => (
          <MenuItem dense key={tag.index}>
            <Tag
              label={tag.tag}
              handleOpenMenu={(e) => handleOptionsMenuOpen(e, tag.index)}
              isEditing={tag.index === editTagId}
              setEditTagId={setEditTagId}
              saveEdit={(newLabel) => saveEdit(tag.index, newLabel)}
            />
          </MenuItem>
        ))}

        {remainingTags.length > 0 && isOwner && <Divider />}
        {isOwner && (
          <Stack direction="row" gap={1} alignItems="center" px={1}>
            <Input
              placeholder="Add new tag"
              size="small"
              onChange={(e) => setNewTag(e.target.value)}
              value={newTag}
              onKeyDown={(e) => e.stopPropagation()}
            />
            <Button variant="outlined" color="primary" size="small" onClick={handleCreateTag}>
              Save
            </Button>
          </Stack>
        )}
      </Menu>
    );
  };

  const renderEmptyTags = () => {
    if (isOwner) {
      return null;
    }
    return <Typography variant="body1">No tags available</Typography>;
  };

  return (
    <WhiteWrapper mb="24px">
      <ChipsWrapper ref={wrapperRef}>
        {tags?.length === 0 && renderEmptyTags()}
        {visibleTags?.map((tag) => (
          <Tag
            key={tag.index}
            label={tag.tag}
            handleOpenMenu={(e) => handleOptionsMenuOpen(e, tag.index)}
            isEditing={tag.index === editTagId}
            setEditTagId={setEditTagId}
            saveEdit={(newLabel) => saveEdit(tag.index, newLabel)}
          />
        ))}
        {remainingTagsCount > 0 ? (
          <Tag label={`+${remainingTagsCount}`} handleOpenMenu={() => setRemainingMenuOpen(true)} />
        ) : (
          isOwner && (
            <IconButton sx={{ padding: 0 }} onClick={() => setRemainingMenuOpen(true)}>
              <AddIcon />
            </IconButton>
          )
        )}
      </ChipsWrapper>
      {chipOptionsMenu()}
      {remainingChipsMenu()}
    </WhiteWrapper>
  );
};

export default Tags;
